import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthInit } from '@/hooks/useAuth'
import { UserRolesProvider } from '@/contexts/UserRolesContext'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { Layout } from '@/components/layout/Layout'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { DeploymentsPage } from '@/pages/DeploymentsPage'
import { NewDeploymentPage } from '@/pages/NewDeploymentPage'
import { DeploymentDetailPage } from '@/pages/DeploymentDetailPage'
import { ProductsPage } from '@/pages/ProductsPage'
import { AdminPage } from '@/pages/AdminPage'
import { MonitoringPage } from '@/pages/MonitoringPage'
import { ReleasePage } from '@/pages/ReleasePage'
import { StatusCentralPage } from '@/pages/StatusCentralPage'
import { MCPDeploymentPage } from '@/pages/MCPDeploymentPage'
import { AppInAKSPage } from '@/pages/AppInAKSPage'
import { AKSManagementPage } from '@/pages/AKSManagementPage'
import { IBMCloudPage } from '@/pages/IBMCloudPage'
import { HaproxyPage } from '@/pages/HaproxyPage'
import { CloudflarePage } from '@/pages/CloudflarePage'
import { AzureInventoryPage } from '@/pages/AzureInventoryPage'
import { PatchManagementPage } from '@/pages/PatchManagementPage'
import { PatchCampaignFormPage } from '@/pages/PatchCampaignFormPage'
import { PatchCampaignDetailPage } from '@/pages/PatchCampaignDetailPage'
import { CertificatesPage } from '@/pages/CertificatesPage'
import { ServiceDeskPage } from '@/pages/ServiceDeskPage'
import { CostOptimizationPage } from '@/pages/CostOptimizationPage'
import { CostReportsPage } from '@/pages/CostReportsPage'
import { CMDBPage } from '@/pages/CMDBPage'
import { OneClickDeployPage } from '@/pages/OneClickDeployPage'
import { DisasterRecoveryPage } from '@/pages/DisasterRecoveryPage'
import { RPORTOPage } from '@/pages/DisasterRecovery/RPORTOPage'
import { DRDrillsPage } from '@/pages/DisasterRecovery/DRDrillsPage'
import { DrillDetailPage } from '@/pages/DisasterRecovery/DrillDetailPage'
import { DrillTemplatesPage } from '@/pages/DisasterRecovery/DrillTemplatesPage'
import { BackupStatusPage } from '@/pages/DisasterRecovery/BackupStatusPage'
import { ReportsPage } from '@/pages/DisasterRecovery/ReportsPage'
import { NotificationsPage } from '@/pages/DisasterRecovery/NotificationsPage'
import { DRAdminPage } from '@/pages/DisasterRecovery/DRAdminPage'
import { UserRolesPage } from '@/pages/UserRolesPage'
import { TeamManagementPage } from '@/pages/TeamManagementPage'
import ProductOnboardingPage from '@/pages/ProductOnboardingPage'

export default function App() {
  useAuthInit()

  return (
    <UserRolesProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* ── Core ── */}
          <Route path="dashboard"          element={<DashboardPage />} />
          <Route path="ibm-cloud"          element={<IBMCloudPage />} />

          {/* ── Disaster Recovery ── */}
          <Route path="disaster-recovery"                    element={<DisasterRecoveryPage />} />
          <Route path="disaster-recovery/rpo-rto"            element={<RPORTOPage />} />
          <Route path="disaster-recovery/drills"             element={<DRDrillsPage />} />
          <Route path="disaster-recovery/drills/:id"         element={<DrillDetailPage />} />
          <Route path="disaster-recovery/drill-templates"    element={<DrillTemplatesPage />} />
          <Route path="disaster-recovery/backup"             element={<BackupStatusPage />} />
          <Route path="disaster-recovery/reports"            element={<ReportsPage />} />
          <Route path="disaster-recovery/notifications"      element={<NotificationsPage />} />
          <Route path="disaster-recovery/admin"              element={<DRAdminPage />} />

          {/* ── Deployments ── */}
          <Route path="deployments"         element={<DeploymentsPage />} />
          <Route path="deployments/new"     element={<NewDeploymentPage />} />
          <Route path="deployments/:id"     element={<DeploymentDetailPage />} />
          <Route path="onboarding/new"      element={<ProductOnboardingPage />} />
          <Route path="app-in-aks"          element={<AppInAKSPage />} />
          <Route path="one-click-deploy"    element={<OneClickDeployPage />} />
          <Route path="mcp-deployment"      element={<MCPDeploymentPage />} />

          {/* ── Operations ── */}
          <Route path="release-management"  element={<ReleasePage />} />
          <Route path="patch-management"    element={<PatchManagementPage />} />
          <Route path="patch-management/azure" element={<AzureInventoryPage />} />
          <Route path="patch-management/new" element={<PatchCampaignFormPage />} />
          <Route path="patch-management/:campaignId" element={<PatchCampaignDetailPage />} />
          <Route path="aks-management"      element={<AKSManagementPage />} />

          {/* ── Infrastructure ── */}
          <Route path="haproxy"             element={<HaproxyPage />} />
          <Route path="cloudflare"          element={<CloudflarePage />} />
          <Route path="certificates"        element={<CertificatesPage />} />

          {/* ── Observability ── */}
          <Route path="status-central"      element={<StatusCentralPage />} />
          <Route path="monitoring"          element={<MonitoringPage />} />

          {/* ── Financial ── */}
          <Route path="cost-optimization"   element={<CostOptimizationPage />} />
          <Route path="cost-reports"        element={<CostReportsPage />} />

          {/* ── CMDB ── */}
          <Route path="cmdb"                element={<CMDBPage />} />

          {/* ── Support ── */}
          <Route path="service-desk"        element={<ServiceDeskPage />} />

          {/* ── Settings ── */}
          <Route path="products"            element={<ProductsPage />} />
          <Route path="admin"               element={<AdminPage />} />
          <Route path="user-roles"          element={<UserRolesPage />} />
          <Route path="teams"               element={<TeamManagementPage />} />
        </Route>
      </Routes>
    </UserRolesProvider>
  )
}
